import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import VerticalSpace from "../../Components/VerticalSpace";
import Toast from "../../Components/Common/Toast";
import { baseURL, postRequest } from "../../api/apiClient";
import HelmetWrapper from "../../Components/Common/HelmetWrapper";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";

const ChangePassword: React.FC = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      Toast("All fields are required", "error");
      return;
    }

    if (newPassword.length < 8) {
      Toast("New password must be at least 8 characters long", "error");
      return;
    }

    if (newPassword !== confirmPassword) {
      Toast("New passwords do not match", "error");
      return;
    }

    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
      Toast(
        "Password must contain uppercase, lowercase, number and special character",
        "error"
      );
      return;
    }

    try {
      setIsLoading(true);
      const response = await postRequest(`${baseURL}/change-password`, {
        oldPassword,
        newPassword,
      });

      if (response?.status === 200) {
        Toast(response?.data?.statusMessage || response?.data?.message || "Password changed successfully", "success");
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleChangePassword();
    }
  };

  return (
    <>
      <HelmetWrapper title="Lexik- Change Password" />
      <Container>
        <ColumnContainer gap="20px" alignItem="center" justifyContent="center" style={{ minHeight: "80vh" }}>
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "30px",
              width: "100%",
              maxWidth: "600px",
            }}
          >
            <ColumnContainer gap="20px">
              <Label fontSize="24px" fontWeight={600}>
                Change Password
              </Label>

              <VerticalSpace height={10} />

              {/* Old Password */}
              <div className="form-group" style={{ width: "100%" }}>
                <Label fontSize="14px" fontWeight={500} style={{ marginBottom: "8px" }}>
                  Current Password
                </Label>
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type={showOldPassword ? "text" : "password"}
                    className="form-control"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    style={{
                      height: "50px",
                      borderRadius: "8px",
                      border: "1px solid #D9D9D9",
                      fontSize: "14px",
                      paddingRight: "40px",
                      width: "100%",
                    }}
                  />
                  <span
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                    }}
                  >
                    {showOldPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </span>
                </div>
              </div>

              {/* New Password */}
              <div className="form-group" style={{ width: "100%" }}>
                <Label fontSize="14px" fontWeight={500} style={{ marginBottom: "8px" }}>
                  New Password
                </Label>
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    style={{
                      height: "50px",
                      borderRadius: "8px",
                      border: "1px solid #D9D9D9",
                      fontSize: "14px",
                      paddingRight: "40px",
                      width: "100%",
                    }}
                  />
                  <span
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                    }}
                  >
                    {showNewPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </span>
                </div>
                <small style={{ color: "#666", fontSize: "12px", marginTop: "5px", display: "block" }}>
                  Password must be at least 8 characters with uppercase, lowercase, number and special character
                </small>
              </div>

              {/* Confirm Password */}
              <div className="form-group" style={{ width: "100%" }}>
                <Label fontSize="14px" fontWeight={500} style={{ marginBottom: "8px" }}>
                  Confirm New Password
                </Label>
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Confirm new password"
                    style={{
                      height: "50px",
                      borderRadius: "8px",
                      border: "1px solid #D9D9D9",
                      fontSize: "14px",
                      paddingRight: "40px",
                      width: "100%",
                    }}
                  />
                  <span
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                    }}
                  >
                    {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </span>
                </div>
              </div>

              <VerticalSpace height={10} />

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "15px",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="btn"
                  style={{
                    backgroundColor: "#592ACB",
                    borderRadius: "10px",
                    padding: "12px 40px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#fff",
                    border: "none",
                    minWidth: "150px",
                    height: "48px",
                    flex: "1",
                  }}
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{
                    backgroundColor: "#FF9E00",
                    borderRadius: "10px",
                    padding: "12px 40px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#fff",
                    border: "none",
                    minWidth: "230px",
                    height: "48px",
                    flex: "1",
                  }}
                  onClick={handleChangePassword}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only"></span>
                      </div>
                    </div>
                  ) : (
                    "Change Password"
                  )}
                </button>
              </div>
            </ColumnContainer>
          </div>
        </ColumnContainer>
      </Container>
    </>
  );
};

export default ChangePassword;
